import { AnimatePresence } from 'framer-motion';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { CloseIcon, CustomizeIcon, ExportIcon, SearchIcon } from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import {
  EmptyState,
  EmptyStateImageVariant,
  EmptyStateVariant,
} from '../../../components/empty-state/index';
import { Input } from '../../../components/input/index';
import { CrossfadePanel } from '../../../components/motion/index';
import {
  AnalyticHooks,
  SnapshotHooks,
  useDebounce,
  useEventOutsideElement,
  useLocale,
  useModalState,
} from '../../../hooks/index';
import { capTableStore } from '../../../storage/local/index';
import { PossibleAction } from '../../../types/google-analytics-types.types';
import {
  CapTableOrdering,
  Column,
  DetailsSortVariant,
  detailsSortVariantTitleMap,
  Snapshot,
  ViewBy,
} from '../../../types/snapshot.types';
import { ShareCapTableButton } from '../share/share-cap-table-button';
import { normalizeCapTableOrdering } from '../utils/sort-cap-table-rows.util';
import { DetailsCustomizeView } from './details-customize-view';
import { capTableScrollAreaClass } from './table-variants/cap-table-sticky-classes';
import { Table } from './table-variants/table';

export type TableProps = {
  companyId: string;
  isEmptySearchResult: boolean;
  ordering: CapTableOrdering;
  search: string;
  setEmptySearchResult: (state: boolean) => void;
  snapshot?: Snapshot;
  type: DetailsSortVariant;
  viewBy: Record<ViewBy, boolean>;
  visibleColumns: Column[];
};

export type DetailsProps = {
  className?: string;
  companyId: string;
  isLoadingFinished: boolean;
  snapshot?: Snapshot;
  snapshotPanelKey?: string;
};

export type CustomView = Record<
  DetailsSortVariant,
  {
    columns: Column[];
    viewBy: Record<ViewBy, boolean>;
  }
>;

/** What the Customize View modal hands back on Save — one tab's worth of settings each. */
export type CustomizeViewResult = {
  /** Whether Save also persisted this view as the default, or applied it to this session only. */
  isDefaultView: boolean;
  ordering: CapTableOrdering;
  view: CustomView;
};

const customViewFieldList = Object.values(Column);

const defaultCustomView = {
  [DetailsSortVariant.BY_SHARE_CLASS]: {
    columns: customViewFieldList,
    viewBy: {
      [ViewBy.STAKEHOLDERS]: true,
      [ViewBy.TYPE]: true,
    },
  },
  [DetailsSortVariant.BY_STAKEHOLDER]: {
    columns: customViewFieldList,
    viewBy: {
      [ViewBy.STAKEHOLDERS]: true,
      [ViewBy.TYPE]: true,
    },
  },
};

export const DesktopDetails: FC<DetailsProps> = ({
  companyId,
  className,
  snapshot,
  isLoadingFinished,
  snapshotPanelKey = 'latest',
}) => {
  const [search, setSearch] = useState<null | string>(null);
  const [isEmptySearchResult, setEmptySearchResult] = useState(false);
  const { messagesLocale } = useLocale();
  const [isOpenSearch, setOpenSearch] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const [isFocusedInput, setInputFocus] = useState(false);

  const [selectedDetailsSortVariant, setDetailsSortVariant] = useState<DetailsSortVariant>(
    DetailsSortVariant.BY_STAKEHOLDER,
  );
  const [customView, setCustomView] = useState<CustomView>({
    [DetailsSortVariant.BY_SHARE_CLASS]:
      capTableStore.get()?.[DetailsSortVariant.BY_SHARE_CLASS] ||
      defaultCustomView[DetailsSortVariant.BY_SHARE_CLASS],
    [DetailsSortVariant.BY_STAKEHOLDER]:
      capTableStore.get()?.[DetailsSortVariant.BY_STAKEHOLDER] ||
      defaultCustomView[DetailsSortVariant.BY_STAKEHOLDER],
  });
  // A persisted order can predate a stakeholder type the product has since gained, so it is
  // repaired on the way in rather than trusted as stored.
  const [ordering, setOrdering] = useState<CapTableOrdering>(() =>
    normalizeCapTableOrdering(capTableStore.get()?.ordering),
  );
  // Session-only on purpose: a reload drops the unsaved view and shows the saved default again, so
  // the checkbox is right to start checked then.
  const [isDefaultView, setDefaultView] = useState(true);
  const capTableExportTracker = AnalyticHooks.useClick(PossibleAction.DOWNLOAD_FILE);

  const { isPending, mutateExport } = SnapshotHooks.useExport({ companyId, snapshotId: '' });

  const { isOpen, toggler, handleOpenModal, handleCloseModal, handleSuccessModal } =
    useModalState<CustomizeViewResult>({
      onSuccess: (settings) => {
        if (!settings) return;
        toast.success(<AppFormattedMessage id="CHANGES_SAVED" />);
        setCustomView(settings.view);
        setOrdering(settings.ordering);
        setDefaultView(settings.isDefaultView);
      },
    });

  const { debounceCallback } = useDebounce({
    callback: () => {
      invalidateQuery();
    },
  });

  // Reset while rendering rather than in an effect: an empty result unmounts the table, so the
  // view can no longer clear its own flag, and an effect here would race the view's decision.
  const emptySearchResetKey = `${search ?? ''}|${selectedDetailsSortVariant}`;
  const [lastEmptySearchResetKey, setLastEmptySearchResetKey] = useState(emptySearchResetKey);

  if (lastEmptySearchResetKey !== emptySearchResetKey) {
    setLastEmptySearchResetKey(emptySearchResetKey);
    setEmptySearchResult(false);
  }

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      debounceCallback(value);
    },
    [debounceCallback],
  );
  useEventOutsideElement(inputRef, 'click', () => !search && setOpenSearch(false));

  const invalidateQuery = useCallback(() => {}, []);

  const hasShareClassData = Boolean(
    snapshot?.stats?.shareClasses?.some(({ diluted, issued }) => diluted > 0 || issued > 0),
  );
  const hasStakeholderData = Boolean(
    snapshot?.stats?.stakeholders?.some(
      ({ diluted, issued, invested }) => diluted > 0 || issued > 0 || invested > 0,
    ),
  );
  const isCapTableEmpty =
    !hasShareClassData && !hasStakeholderData && !((snapshot?.issuedShares ?? 0) > 0);

  // The Ordering tab lists what this cap table holds, not the whole catalogue of types — a row for
  // a type the company has none of would reorder nothing the viewer can see.
  const availableStakeholderTypes = useMemo(
    () =>
      (snapshot?.stats?.stakeholdersTypes ?? [])
        .filter(({ diluted }) => diluted > 0)
        .map(({ type }) => type),
    [snapshot],
  );

  const customViewPanelKey = useMemo(() => {
    const { viewBy, columns } = customView[selectedDetailsSortVariant];

    return [
      selectedDetailsSortVariant,
      viewBy.stakeholders ? 'stakeholders' : '',
      viewBy.type ? 'type' : '',
      ...[...columns].sort(),
      ordering.mode,
      ordering.withinType,
      ...ordering.typeOrder,
    ].join('|');
  }, [customView, ordering, selectedDetailsSortVariant]);

  return (
    <>
      <AlertDialogWrapper control={{ open: isOpen, onOpenChange: toggler }}>
        <DetailsCustomizeView
          availableTypes={availableStakeholderTypes}
          isDefaultView={isDefaultView}
          onCancel={handleCloseModal}
          onSuccess={handleSuccessModal}
          ordering={ordering}
          selectedForTab={selectedDetailsSortVariant}
          viewSettings={customView}
        />
      </AlertDialogWrapper>

      <div className={twMerge('flex min-w-0 flex-col rounded-md px-4 pb-4 shadow-sm', className)}>
        <span className="p-4 text-base font-semibold text-gray-700">
          <AppFormattedMessage id="DETAILS" />
        </span>
        <div className="flex h-fit w-full min-w-0 flex-col rounded-md shadow-sm">
          <div
            className={twMerge(
              'flex w-full items-center justify-between p-4',
              isCapTableEmpty && 'pointer-events-none opacity-25',
            )}
          >
            <div className="flex h-9 divide-x divide-gray-200 rounded border border-gray-200">
              {detailsSortVariantTitleMap.map(([filterKey, filter]) => (
                <Button
                  className={twMerge(
                    'h-full w-fit rounded-none px-4 text-sm font-[450] text-[#858593] transition-colors duration-200',
                    selectedDetailsSortVariant === filterKey &&
                      'bg-brand-25 text-sm font-[550] text-[#172335]',
                  )}
                  key={filterKey}
                  onClick={() => setDetailsSortVariant(filterKey)}
                  styleType="NONE"
                >
                  <AppFormattedMessage id={filter} />
                </Button>
              ))}
            </div>
            <div className="relative flex gap-3">
              <div className="flex items-center justify-end" ref={inputRef}>
                <Input
                  inputClassName="h-9"
                  onBlur={() => setInputFocus(false)}
                  onChange={handleSearch}
                  onFocus={() => {
                    setInputFocus(true);
                  }}
                  placeholder={search || isFocusedInput ? '' : <AppFormattedMessage id="SEARCH" />}
                  value={search || ''}
                  wrapperClassName={twMerge(
                    'transition-all duration-500 w-[200px] overflow-hidden h-9',
                    isOpenSearch ? 'delay-500' : 'w-0',
                  )}
                />
                {isOpenSearch && typeof search === 'string' && (
                  <div
                    className="absolute mr-10 cursor-pointer"
                    onClick={() => {
                      setSearch('');
                      invalidateQuery();
                    }}
                  >
                    <CloseIcon />
                  </div>
                )}
                <Button
                  className={twMerge(
                    'size-9 border-[1px] border-gray-100 bg-gray-50 transition-all duration-300',
                    isOpenSearch
                      ? 'h-9 rounded-l-none rounded-br-none border-x-0 border-t-0 border-b-black'
                      : 'delay-500',
                    isFocusedInput && 'border-b-[2px] border-b-brand-700 transition-none',
                  )}
                  onClick={() => {
                    setOpenSearch((prev) => !prev);
                    setSearch(null);
                    invalidateQuery();
                  }}
                >
                  <SearchIcon iconColor={isFocusedInput ? '#2565C8' : '#344054'} />
                </Button>
              </div>
              <Button
                className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
                disabled={isPending}
                isLoading={isPending}
                onClick={() =>
                  mutateExport(
                    {
                      // The spreadsheet is the table taken away with you, so it follows the same
                      // Customize View settings — columns, view-by, and now ordering.
                      ordering,
                      viewByColumns: Object.values(DetailsSortVariant).reduce(
                        (acc, curr) => ({ ...acc, [curr]: customView[curr].viewBy }),
                        {},
                      ) as Record<DetailsSortVariant, Record<ViewBy, boolean>>,

                      viewColumns: Object.values(DetailsSortVariant).reduce(
                        (acc, curr) => ({ ...acc, [curr]: customView[curr].columns }),
                        {},
                      ) as Record<DetailsSortVariant, Column[]>,
                    },
                    { onSuccess: () => capTableExportTracker('Cap Table') },
                  )
                }
              >
                <ExportIcon className="rotate-180" />
                <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                  <AppFormattedMessage id="EXPORT" />
                </span>
              </Button>

              <ShareCapTableButton />

              <Button
                className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
                onClick={handleOpenModal}
              >
                <CustomizeIcon />
                <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                  <AppFormattedMessage id="CUSTOMIZE_VIEW" />
                </span>
              </Button>
            </div>
          </div>

          {isCapTableEmpty ? (
            <EmptyState
              colorScheme={EmptyStateVariant.BLUE}
              description={messagesLocale['CAP_TABLE_DETAILS']}
              imageVariant={EmptyStateImageVariant.FLASHLIGHT}
              text={isLoadingFinished ? messagesLocale['NOTHING_HERE'] : messagesLocale['LOADING']}
            />
          ) : (
            <AnimatePresence mode="wait">
              <CrossfadePanel
                className="w-full min-w-0"
                panelKey={`${snapshotPanelKey}-${customViewPanelKey}`}
                translate={false}
              >
                <div className={capTableScrollAreaClass} data-cap-table-h-scroll>
                  <Table
                    companyId={companyId}
                    isEmptySearchResult={isEmptySearchResult}
                    ordering={ordering}
                    search={search || ''}
                    setEmptySearchResult={setEmptySearchResult}
                    snapshot={snapshot}
                    type={selectedDetailsSortVariant}
                    viewBy={customView[selectedDetailsSortVariant].viewBy}
                    visibleColumns={customView[selectedDetailsSortVariant].columns}
                  />
                </div>
              </CrossfadePanel>
            </AnimatePresence>
          )}
        </div>
      </div>
    </>
  );
};
